/**
 * RegistrarAssignmentCard - Facility assigns/removes clinic registrars per clinic (Phase 4).
 * Only facility (and chartmind-admin) can write; chartmind-supervisor can read.
 */

import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Delete, PersonAdd } from '@mui/icons-material';
import { getRegistrars, assignRegistrar, removeRegistrar } from '@healthdesk/shared-services';

const RegistrarAssignmentCard = ({ clinicId, currentUserId, canWrite }) => {
  const [registrars, setRegistrars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newUserId, setNewUserId] = useState('');

  const load = () => {
    if (!clinicId) return;
    getRegistrars(clinicId).then(setRegistrars).catch((err) => setError(err.message)).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!clinicId) {
      setLoading(false);
      return;
    }
    load();
  }, [clinicId]);

  const handleAdd = async () => {
    const uid = (newUserId || '').trim();
    if (!uid || !clinicId || !currentUserId || !canWrite) return;
    setSaving(true);
    setError(null);
    try {
      await assignRegistrar(clinicId, uid, currentUserId);
      setNewUserId('');
      setSuccess('Registrar assigned.');
      setTimeout(() => setSuccess(null), 3000);
      try {
        const fn = httpsCallable(getFunctions(), 'writeClinicAuditEvent');
        await fn({ action: 'registrar_assign', resourceId: clinicId, metadata: { userId: uid } });
      } catch (e) { /* ignore */ }
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (userId) => {
    if (!clinicId || !canWrite) return;
    setSaving(true);
    setError(null);
    try {
      await removeRegistrar(clinicId, userId);
      setSuccess('Registrar removed.');
      setTimeout(() => setSuccess(null), 3000);
      try {
        const fn = httpsCallable(getFunctions(), 'writeClinicAuditEvent');
        await fn({ action: 'registrar_remove', resourceId: clinicId, metadata: { userId } });
      } catch (e) { /* ignore */ }
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!clinicId) return null;
  if (loading) {
    return (
      <Card sx={{ mt: 2 }}>
        <CardContent><CircularProgress size={24} /> Loading...</CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title="Clinic registrars"
        subheader="Users who can edit the welcome message and instructions for this clinic. Only facility can assign or remove."
      />
      <CardContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {canWrite && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              placeholder="User ID to add as registrar"
              value={newUserId}
              onChange={(e) => setNewUserId(e.target.value)}
            />
            <Button variant="outlined" startIcon={saving ? <CircularProgress size={16} /> : <PersonAdd />} onClick={handleAdd} disabled={saving || !newUserId.trim()}>
              Add
            </Button>
          </Box>
        )}
        <List dense>
          {registrars.length === 0 && <Typography variant="body2" color="text.secondary">No registrars assigned.</Typography>}
          {registrars.map((r) => (
            <ListItem
              key={r.userId}
              secondaryAction={
                canWrite && (
                  <IconButton edge="end" size="small" onClick={() => handleRemove(r.userId)} disabled={saving}>
                    <Delete />
                  </IconButton>
                )
              }
            >
              <ListItemText primary={r.userId} secondary={r.assignedAt ? `Assigned ${r.assignedAt?.toDate?.()?.toLocaleDateString?.() || '—'}` : ''} />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default RegistrarAssignmentCard;
